import type { PropsWithChildren } from "react";
import { useCallback, useMemo, useState } from "react";

import { AppContext, type Control } from "./AppContext";

type Props = Pick<AppContext, "headerBar">;

export const AppContextProvider = ({ headerBar, children }: PropsWithChildren<Props>) => {
  const [viewBars, setViewBars] = useState<Control[]>([]);
  const [toolBar, setToolBar] = useState<Control | null>(null);
  const [actionBar, setActionBar] = useState<Control | null>(null);

  const setViewBar = useCallback((control: Control | null) => {
    if (!control) return;
    // Key by id so mounts/unmounts of different view bars don't clobber each other. Update an
    // existing entry in place (keeps stack order); otherwise append so the newest shows on top.
    setViewBars((prev) => {
      const index = prev.findIndex((existing) => existing.id === control.id);
      if (index === -1) return [...prev, control];
      return prev.with(index, control);
    });
  }, []);

  const clearViewBar = useCallback((id: string) => {
    setViewBars((prev) => prev.filter((control) => control.id !== id));
  }, []);

  const getMostRecentViewBar = useMemo(() => {
    return viewBars[viewBars.length - 1] ?? null;
  }, [viewBars]);

  const value = useMemo(
    () => ({
      headerBar,
      viewBar: getMostRecentViewBar,
      setViewBar,
      clearViewBar,
      toolBar,
      setToolBar,
      actionBar,
      setActionBar,
    }),
    [actionBar, clearViewBar, getMostRecentViewBar, headerBar, setViewBar, toolBar],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
